import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { FaTrash } from "react-icons/fa";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { useNavigate, useParams } from "react-router";
import {
  useCreateContractInvoiceMutation,
  useGetCompanyQuery,
  useGetContractInvoicesQuery,
  useGetCustomerQuery,
  useGetNextContractInvoiceIdQuery,
  useLazyGetContractInvoiceSuggestByDateQuery,
  useLazyGetContractLookupQuery,
  useLazyGetContractsQuery,
  useLazyGetContractByIdQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { fmtCustomerName } from "../../../Core/utils/nameUtils";
import { useStyles } from "../../../theme/appStyles";
import { IContractInvoiceEntry, ICustomer, ICompanyDetails, IContract } from "../../../Api/Interface/api.interface";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { uploadPdf } from "../../../Components/UploadFile/upload";
import CrmClientSelect from "../../../Components/CrmClientSelect";

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt = (v: number) => `$${v.toFixed(2)}`;
const DEFAULT_TAX = 13;

const emptyEntry = (): IContractInvoiceEntry => ({
  entry_date: null,
  description: "",
  quantity: 1,
  unit_price: 0,
  tax_rate: DEFAULT_TAX,
  tax_amount: 0,
  total: 0,
});

const calcEntry = (e: IContractInvoiceEntry): IContractInvoiceEntry => {
  const lineBase = e.quantity * e.unit_price;
  const taxAmount = parseFloat((lineBase * (e.tax_rate / 100)).toFixed(2));
  const total = parseFloat((lineBase + taxAmount).toFixed(2));
  return { ...e, tax_amount: taxAmount, total };
};


// ── Component ──────────────────────────────────────────────────────────────────
const CreateContractInvoice: React.FC = () => {
  const { id, clientId, contractId } = useParams<{ id?: string; clientId?: string; contractId?: string }>();
  const navigate = useNavigate();
  const classes = useStyles();
  const { showMessage } = useNotifier();

  const isEdit = Boolean(id);

  // ── API hooks ────────────────────────────────────────────────────────────────
  const [createContractInvoice, { isLoading: isSaving }] = useCreateContractInvoiceMutation();
  const [fetchSuggestions]    = useLazyGetContractInvoiceSuggestByDateQuery();
  const [fetchContractLookup] = useLazyGetContractLookupQuery();
  const [fetchAllContracts, { isLoading: isLoadingTemplates }] = useLazyGetContractsQuery();
  const [fetchContractById] = useLazyGetContractByIdQuery();

  const { data: customerData } = useGetCustomerQuery({ searchText: "", id: "", quoteWorkOrderCustomerId: "" });
  const { data: companyData }  = useGetCompanyQuery({ searchText: "", id: "" });
  const { data: nextIdData }   = useGetNextContractInvoiceIdQuery(undefined, { skip: isEdit });

  const { data: existingData, isFetching: isLoadingExisting } = useGetContractInvoicesQuery(
    { id: id ?? "" },
    { skip: !isEdit }
  );

  // ── Header state ─────────────────────────────────────────────────────────────
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer | null>(null);
  const [selectedCompany, setSelectedCompany]   = useState<ICompanyDetails | null>(null);
  const [invoiceNo, setInvoiceNo]               = useState("");
  const [issueDate, setIssueDate]               = useState<Dayjs | null>(dayjs());
  const [poNumber, setPoNumber]                 = useState("");
  const [poDocument, setPoDocument]             = useState<string | null>(null);
  const [poFileName, setPoFileName]             = useState<string>("");
  const [poUploading, setPoUploading]           = useState(false);
  const [memo, setMemo]                         = useState("");
  const [discount, setDiscount]                 = useState(0);

  // ── Entry state ──────────────────────────────────────────────────────────────
  const [entries, setEntries]                   = useState<IContractInvoiceEntry[]>([emptyEntry()]);
  const [autoGeneratedRows, setAutoGeneratedRows] = useState<Set<number>>(new Set());

  // ── Dialog state ─────────────────────────────────────────────────────────────
  const [confirmOpen, setConfirmOpen]           = useState(false);
  const [pendingStatus, setPendingStatus]       = useState<1 | 2>(1);

  // ── Finalize validation state ─────────────────────────────────────────────────
  const [submitted, setSubmitted]               = useState(false);
  const rowRefs                                 = useRef<(HTMLTableRowElement | null)[]>([]);

  // ── Auto-fill state (header — from previous invoices) ───────────────────────
  const [autoFillLoading, setAutoFillLoading]   = useState(false);
  const [autoFilled, setAutoFilled]             = useState(false);
  const [suggestions, setSuggestions]           = useState<any[]>([]);
  const [suggestOpen, setSuggestOpen]           = useState(false);

  // ── Contract auto-load state ─────────────────────────────────────────────────
  const [contractLoading, setContractLoading]   = useState(false);
  const [contractLoaded, setContractLoaded]     = useState(false);
  const [loadedContractName, setLoadedContractName] = useState("");
  const [loadedContract, setLoadedContract]     = useState<IContract | null>(null);
  const [pendingContracts, setPendingContracts] = useState<IContract[]>([]);
  const [pendingContractDate, setPendingContractDate] = useState<Dayjs | null>(null);
  const [contractSelectOpen, setContractSelectOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const customers = [...((customerData?.data as ICustomer[]) ?? [])].sort((a, b) =>
    fmtCustomerName(a).localeCompare(fmtCustomerName(b))
  );
  const companies = (companyData?.data as ICompanyDetails[]) ?? [];

  // ── Auto-select client from URL param ────────────────────────────────────────
  useEffect(() => {
    if (!clientId || !customerData?.data) return;
    const found = customers.find((c) => String(c.id) === String(clientId));
    if (found) setSelectedCustomer(found);
  }, [clientId, customerData]);

  // ── Auto-select default company on create (matches header-selected company) ──
  useEffect(() => {
    if (isEdit) return;
    if (selectedCompany) return;
    if (!companyData?.data) return;
    const list = (companyData.data as ICompanyDetails[]) ?? [];
    if (list.length === 0) return;
    const defaultCo = list.find((c) => Number((c as any).isDefault) === 1) || list[0];
    if (defaultCo) setSelectedCompany(defaultCo);
  }, [isEdit, companyData, selectedCompany]);

  // ── Auto-load contract from URL param (dashboard "Generate Invoice") ──────────
  useEffect(() => {
    if (!contractId || !customerData?.data) return;
    loadAndApplyContract(Number(contractId), issueDate ?? dayjs());
  }, [contractId, customerData]);

  // ── Populate next ID on create ───────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit && nextIdData?.data) {
      const d = nextIdData.data as any;
      setInvoiceNo(d.contract_invoice_no ?? "");
    }
  }, [isEdit, nextIdData]);

  // ── Populate form on edit ────────────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit || !existingData?.data) return;
    const list = existingData.data as any[];
    if (!list || list.length === 0) return;
    const inv = list[0];

    setInvoiceNo(inv.contract_invoice_no ?? "");
    setIssueDate(inv.issue_date ? dayjs(inv.issue_date) : null);
    setPoNumber(inv.po_number ?? "");
    setPoDocument(inv.po_document ?? null);
    setPoFileName(inv.po_document ? inv.po_document.split("/").pop() ?? "" : "");
    setMemo(inv.memo ?? "");
    setDiscount(parseFloat(inv.discount ?? 0));

    const c = customers.find((x) => String(x.id) === String(inv.customer_id));
    if (c) setSelectedCustomer(c);

    const co = companies.find((x) => String(x.id) === String(inv.company_id));
    if (co) setSelectedCompany(co);

    if (inv.entries && inv.entries.length > 0) {
      setEntries(
        inv.entries.map((e: any) => ({
          id: e.id,
          entry_date: e.entry_date ?? null,
          description: e.description ?? "",
          quantity: parseInt(e.quantity ?? 1, 10),
          unit_price: parseFloat(e.unit_price ?? 0),
          tax_rate: parseFloat(e.tax_rate ?? DEFAULT_TAX),
          tax_amount: parseFloat(e.tax_amount ?? 0),
          total: parseFloat(e.total ?? 0),
        }))
      );
    }
  }, [isEdit, existingData, customerData, companyData]);

  // ── Totals ───────────────────────────────────────────────────────────────────
  const { subtotal, taxTotal, grandTotal } = useMemo(() => {
    let subtotal = 0, taxTotal = 0;
    entries.forEach((e) => {
      subtotal += e.quantity * e.unit_price;
      taxTotal += e.tax_amount;
    });
    subtotal  = parseFloat(subtotal.toFixed(2));
    taxTotal  = parseFloat(taxTotal.toFixed(2));
    const grandTotal = parseFloat((subtotal + taxTotal - discount).toFixed(2));
    return { subtotal, taxTotal, grandTotal };
  }, [entries, discount]);

  // ── Entry helpers ────────────────────────────────────────────────────────────
  const updateEntry = (idx: number, field: keyof IContractInvoiceEntry, value: any) => {
    setEntries((prev) => {
      const updated = [...prev];
      updated[idx] = calcEntry({ ...updated[idx], [field]: value });
      return updated;
    });
    // User edited row — no longer "auto generated"
    setAutoGeneratedRows((prev) => {
      const next = new Set(prev);
      next.delete(idx);
      return next;
    });
  };

  const addRow = () => {
    setEntries((prev) => [...prev, emptyEntry()]);
    setSubmitted(false);
  };

  const removeRow = (idx: number) => {
    setEntries((prev) => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));
    setAutoGeneratedRows((prev) => {
      const next = new Set<number>();
      prev.forEach((i) => { if (i < idx) next.add(i); else if (i > idx) next.add(i - 1); });
      return next;
    });
  };

  // ── PO upload ────────────────────────────────────────────────────────────────
  const handlePoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPoUploading(true);
    try {
      const path = await uploadPdf(file);
      setPoDocument(path);
      setPoFileName(file.name);
    } catch {
      showMessage("Failed to upload PO document", "error");
    } finally {
      setPoUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ── Contract auto-load ───────────────────────────────────────────────────────
  const applyContract = (contract: IContract, date: Dayjs) => {
    const items = contract.items ?? [];
    if (items.length === 0) { setContractSelectOpen(false); return; }

    const newEntries: IContractInvoiceEntry[] = items.map((item) => {
      const qty   = Number(item.buses ?? 1);
      const price = parseFloat(String(item.unit_price ?? 0));
      const tax   = parseFloat(String(item.tax_percent ?? DEFAULT_TAX));
      const base  = qty * price;
      const taxAmt = parseFloat((base * (tax / 100)).toFixed(2));
      return {
        entry_date:  date.format("YYYY-MM-DD"),
        description: item.description ?? "",
        quantity:    qty,
        unit_price:  price,
        tax_rate:    tax,
        tax_amount:  taxAmt,
        total:       parseFloat((base + taxAmt).toFixed(2)),
      };
    });

    setEntries(newEntries);
    setAutoGeneratedRows(new Set(newEntries.map((_, i) => i)));
    setLoadedContract(contract);
    setLoadedContractName(contract.contract_name);
    setContractLoaded(true);
    setContractSelectOpen(false);
  };

  const runContractLookup = async (date: Dayjs, customer: ICustomer) => {
    setContractLoading(true);
    try {
      const res = await fetchContractLookup({
        clientId: String(customer.id),
        date: date.format("YYYY-MM-DD"),
      }).unwrap();
      const contracts = (res.data as IContract[]) ?? [];
      if (contracts.length === 1) {
        applyContract(contracts[0], date);
      } else if (contracts.length > 1) {
        setPendingContracts(contracts);
        setPendingContractDate(date);
        setContractSelectOpen(true);
      }
      // 0 contracts → do nothing
    } catch {
      // silently ignore — assistive only
    } finally {
      setContractLoading(false);
    }
  };

  const handleReloadContract = () => {
    if (loadedContract && issueDate) applyContract(loadedContract, issueDate);
  };

  // Fetch a contract by ID (guarantees fresh items), then apply it
  const loadAndApplyContract = async (contractId: number, date: Dayjs) => {
    try {
      const res = await fetchContractById(String(contractId)).unwrap();
      const contract = res.data as IContract;
      if (!contract) {
        showMessage("Contract not found.", "error");
        return;
      }
      const items = contract.items ?? [];
      if (items.length === 0) {
        showMessage("This contract template has no line items configured.", "error");
        setContractSelectOpen(false);
        return;
      }
      applyContract(contract, date);
    } catch {
      showMessage("Failed to load contract items.", "error");
    }
  };

  const handleLoadFromTemplate = async () => {
    if (!selectedCustomer) return;
    try {
      const res = await fetchAllContracts({ client_id: String(selectedCustomer.id) }).unwrap();
      const contracts = (res.data as IContract[]) ?? [];
      if (contracts.length === 0) {
        showMessage("No contract templates found for this client", "error");
        return;
      }
      if (contracts.length === 1) {
        await loadAndApplyContract(contracts[0].id, issueDate ?? dayjs());
      } else {
        setPendingContracts(contracts);
        setPendingContractDate(issueDate ?? dayjs());
        setContractSelectOpen(true);
      }
    } catch {
      showMessage("Failed to load contract templates", "error");
    }
  };

  // ── Header auto-fill (from previous invoices) ────────────────────────────────
  // Returns the customer that was auto-filled (or null)
  const applyAutoFillRecord = (record: any): ICustomer | null => {
    let filled: ICustomer | null = null;
    if (record.customer_id) {
      const c = customers.find((x) => String(x.id) === String(record.customer_id));
      if (c) { setSelectedCustomer(c); filled = c; }
    }
    if (record.po_number) setPoNumber(record.po_number);
    if (record.company_id) {
      const co = companies.find((x) => String(x.id) === String(record.company_id));
      if (co) setSelectedCompany(co);
    }
    setAutoFilled(true);
    setSuggestOpen(false);
    return filled;
  };

  // Called when user picks from the multi-match suggestions dialog
  const applyAutoFill = async (record: any) => {
    const filled = applyAutoFillRecord(record);
    const customerToUse = filled || selectedCustomer;
    if (customerToUse && issueDate && !isEdit) {
      await runContractLookup(issueDate, customerToUse);
    }
  };

  // ── Issue date change — primary trigger ──────────────────────────────────────
  const handleIssueDateChange = async (v: Dayjs | null) => {
    setIssueDate(v);
    setAutoFilled(false);
    setContractLoaded(false);
    setAutoGeneratedRows(new Set());
    if (!v || isEdit) return;

    setAutoFillLoading(true);
    try {
      // Step 1: header auto-fill from previous invoices
      const res = await fetchSuggestions(v.format("YYYY-MM-DD")).unwrap();
      const matches = (res.data as any[]) ?? [];

      let customerForContract = selectedCustomer;

      if (matches.length === 1) {
        const filled = applyAutoFillRecord(matches[0]);
        customerForContract = filled ?? selectedCustomer;
      } else if (matches.length > 1) {
        setSuggestions(matches);
        setSuggestOpen(true);
        // Contract lookup uses already-selected customer (if any); runs after user picks from dialog
      }

      // Step 2: contract lookup (run immediately if we have a customer)
      if (customerForContract) {
        await runContractLookup(v, customerForContract);
      }
    } catch {
      // silently ignore
    } finally {
      setAutoFillLoading(false);
    }
  };

  // ── Client change — secondary trigger ────────────────────────────────────────
  const handleClientChange = async (v: ICustomer | null) => {
    setSelectedCustomer(v);
    setAutoFilled(false);
    setContractLoaded(false);
    setAutoGeneratedRows(new Set());
    if (v && issueDate && !isEdit) {
      await runContractLookup(issueDate, v);
    }
  };

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async (status: 1 | 2) => {
    if (!selectedCustomer) { showMessage("Please select a client", "error"); return; }
    if (entries.every((e) => !e.description && e.unit_price === 0)) {
      showMessage("Please add at least one billing entry", "error"); return;
    }
    try {
      const payload = {
        id: isEdit ? id : undefined,
        customer_id: selectedCustomer.id,
        company_id: selectedCompany?.id ?? null,
        status,
        issue_date: issueDate ? issueDate.format("YYYY-MM-DD") : null,
        due_date: null,
        po_number: poNumber || null,
        po_document: poDocument || null,
        memo: memo || null,
        discount,
        entries: entries.map((e, i) => ({ ...e, sort_order: i })),
      };
      const res = await createContractInvoice(payload).unwrap();
      if (res.status) {
        showMessage(status === 2 ? "Invoice finalized successfully" : "Draft saved successfully", "success");
        navigate("/contract-invoice/list");
      } else {
        showMessage(res.message || "Failed to save", "error");
      }
    } catch {
      showMessage("An error occurred while saving", "error");
    }
  };

  // ── Per-row field validity (used both for error display and gate logic) ─────────
  const entryErrors = useMemo(() =>
    entries.map((e) => ({
      date:        !e.entry_date,
      description: e.description.trim().length < 2,
      quantity:    e.quantity <= 0,
      unit_price:  e.unit_price <= 0,
    })),
  [entries]);

  const entriesAreValid = entryErrors.every(
    (err) => !err.date && !err.description && !err.quantity && !err.unit_price
  );

  const openConfirmFinalize = () => {
    if (!selectedCustomer) { showMessage("Please select a client", "error"); return; }

    setSubmitted(true);

    if (!entriesAreValid) {
      showMessage("Please complete all billing entries before finalizing", "error");
      // Scroll to first invalid row
      const firstBadIdx = entryErrors.findIndex(
        (err) => err.date || err.description || err.quantity || err.unit_price
      );
      if (firstBadIdx >= 0) {
        rowRefs.current[firstBadIdx]?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setPendingStatus(2);
    setConfirmOpen(true);
  };

  // ── Combined loading indicator ────────────────────────────────────────────────
  const isLookingUp = autoFillLoading || contractLoading;

  // ── Render ───────────────────────────────────────────────────────────────────
  if (isEdit && isLoadingExisting) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* ── Top bar ── */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={700} color="#0f172a">
              {isEdit ? "Edit Contract Invoice" : "New Contract Invoice"}
            </Typography>
            {invoiceNo && (
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {invoiceNo}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={() => navigate("/contract-invoice/list")}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              startIcon={<SaveAltIcon />}
              onClick={() => handleSave(1)}
              disabled={isSaving}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Save Draft
            </Button>
            <Button
              variant="contained"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={openConfirmFinalize}
              disabled={isSaving}
              sx={{ textTransform: "none", borderRadius: 2, bgcolor: "#2f3891", "&:hover": { bgcolor: "#2f3891" } }}
            >
              {isSaving ? <CircularProgress size={20} color="inherit" /> : "Finalize"}
            </Button>
          </Box>
        </Box>

        {/* ── Header fields ── */}
        <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3, p: 3, mb: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 2fr 1fr 1fr 1fr" },
              gap: 2,
            }}
          >
            {/* 1. Issue Date — primary trigger */}
            <DatePicker
              label="Issue Date"
              value={issueDate}
              onChange={handleIssueDateChange}
              slotProps={{
                textField: {
                  size: "small",
                  InputProps: isLookingUp
                    ? { endAdornment: <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment> }
                    : undefined,
                },
              }}
            />

            {/* 2. Client — secondary trigger */}
            <CrmClientSelect
              customers={customers}
              value={selectedCustomer}
              onChange={handleClientChange}
              label="Client"
              required
              loading={contractLoading}
            />

            {/* 3. PO Number */}
            <TextField
              label="PO Number"
              size="small"
              value={poNumber}
              onChange={(e) => { setPoNumber(e.target.value); setAutoFilled(false); }}
              placeholder="e.g. PO-2024-001"
            />

            {/* 4. Upload PO */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                style={{ display: "none" }}
                onChange={handlePoUpload}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={poUploading ? <CircularProgress size={14} /> : <UploadFileOutlinedIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={poUploading}
                sx={{ textTransform: "none", borderRadius: 2, whiteSpace: "nowrap", height: 40 }}
              >
                {poFileName ? "Change PO" : "Upload PO (optional)"}
              </Button>
              {poFileName && (
                <Tooltip title={poFileName}>
                  <Typography
                    variant="caption"
                    sx={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#2f3891", cursor: "default" }}
                  >
                    {poFileName}
                  </Typography>
                </Tooltip>
              )}
            </Box>

            {/* 5. Company */}
            <Autocomplete
              options={companies}
              getOptionLabel={(c) => c.companyName ?? ""}
              value={selectedCompany}
              onChange={(_, v) => { setSelectedCompany(v); setAutoFilled(false); }}
              renderInput={(params) => (
                <TextField {...params} label="Company (optional)" size="small" />
              )}
            />
          </Box>
        </Paper>

        {/* ── Smart status alerts ── */}
        <Box sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          {autoFilled && (
            <Alert icon={<AutoFixHighOutlinedIcon fontSize="small" />} severity="info" sx={{ borderRadius: 2, py: 0.5 }}>
              Auto-filled based on previous records for this date
            </Alert>
          )}
          {contractLoaded && (
            <Alert
              icon={<AssignmentOutlinedIcon fontSize="small" />}
              severity="success"
              sx={{ borderRadius: 2, py: 0.5 }}
              action={
                <Button
                  size="small"
                  startIcon={<ReplayIcon fontSize="small" />}
                  onClick={handleReloadContract}
                  sx={{ textTransform: "none", color: "inherit", fontWeight: 600 }}
                >
                  Reload
                </Button>
              }
            >
              Contract detected — billing entries loaded automatically for <strong>{loadedContractName}</strong>
            </Alert>
          )}
        </Box>

        {/* ── Entry table ── */}
        <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3, mb: 3, overflow: "hidden" }}>
          <Box
            sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              px: 3, py: 2, borderBottom: "1px solid #e2e8f0",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography fontWeight={600} color="#0f172a">
                Billing Entries
                <Typography component="span" variant="caption" color="text.secondary" ml={1}>
                  ({entries.length} row{entries.length !== 1 ? "s" : ""})
                </Typography>
              </Typography>
              {autoGeneratedRows.size > 0 && (
                <Chip
                  label={`${autoGeneratedRows.size} auto-generated`}
                  size="small"
                  icon={<AssignmentOutlinedIcon style={{ fontSize: 14 }} />}
                  sx={{ bgcolor: "#ede9fe", color: "#2f3891", fontWeight: 600, fontSize: 11 }}
                />
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              {!isEdit && selectedCustomer && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={isLoadingTemplates ? <CircularProgress size={13} color="inherit" /> : <AssignmentOutlinedIcon />}
                  onClick={handleLoadFromTemplate}
                  disabled={isLoadingTemplates}
                  sx={{ textTransform: "none", borderRadius: 2, borderColor: "#2f3891", color: "#2f3891" }}
                >
                  Load from Template
                </Button>
              )}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addRow}
                sx={{ textTransform: "none", bgcolor: "#2f3891", color: "#fff", borderRadius: 2, px: 2, "&:hover": { bgcolor: "#2f3891" } }}
              >
                Add Row
              </Button>
            </Box>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b", width: 200 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b", width: 80 }} align="right">Bus</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b", width: 130 }} align="right">Unit Price</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b", width: 80 }} align="right">Tax</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b", width: 120 }} align="right">Amount</TableCell>
                  <TableCell sx={{ width: 44 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry, idx) => {
                  const isAutoGen = autoGeneratedRows.has(idx);
                  const errs = entryErrors[idx];
                  const showErr = submitted;
                  return (
                    <TableRow
                      key={idx}
                      ref={(el) => { rowRefs.current[idx] = el; }}
                      hover
                      sx={isAutoGen ? { bgcolor: "#f5f3ff", "& td": { borderLeft: "none" } } : undefined}
                    >
                      {/* Date */}
                      <TableCell sx={{ py: 0.5 }}>
                        <DatePicker
                          value={entry.entry_date ? dayjs(entry.entry_date) : null}
                          onChange={(v) => updateEntry(idx, "entry_date", v ? v.format("YYYY-MM-DD") : null)}
                          slotProps={{
                            textField: {
                              size: "small",
                              sx: { width: 185 },
                              error: showErr && errs.date,
                              helperText: showErr && errs.date ? "Required" : undefined,
                            },
                          }}
                        />
                      </TableCell>
                      {/* Description */}
                      <TableCell sx={{ py: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Service description…"
                            value={entry.description}
                            onChange={(e) => updateEntry(idx, "description", e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRow(); } }}
                            error={showErr && errs.description}
                            helperText={showErr && errs.description ? "Required (min 2 chars)" : undefined}
                          />
                          {isAutoGen && (
                            <Chip
                              label="Auto"
                              size="small"
                              sx={{ bgcolor: "#2f3891", color: "#fff", fontSize: 10, height: 20, fontWeight: 700, flexShrink: 0 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      {/* Bus (qty) */}
                      <TableCell sx={{ py: 0.5 }} align="right">
                        <TextField
                          size="small"
                          type="number"
                          inputProps={{ min: 0, step: 1, style: { textAlign: "right" } }}
                          sx={{ width: 72 }}
                          value={entry.quantity}
                          onChange={(e) => updateEntry(idx, "quantity", parseInt(e.target.value) || 0)}
                          error={showErr && errs.quantity}
                          helperText={showErr && errs.quantity ? "Must be > 0" : undefined}
                        />
                      </TableCell>
                      {/* Unit price */}
                      <TableCell sx={{ py: 0.5 }} align="right">
                        <TextField
                          size="small"
                          type="number"
                          inputProps={{ min: 0, step: 0.01, style: { textAlign: "right" } }}
                          sx={{ width: 120 }}
                          value={entry.unit_price === 0 ? "" : entry.unit_price}
                          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                          onChange={(e) => {
                            const num = parseFloat(e.target.value);
                            updateEntry(idx, "unit_price", isNaN(num) ? 0 : num);
                          }}
                          error={showErr && errs.unit_price}
                          helperText={showErr && errs.unit_price ? "Must be > 0" : undefined}
                        />
                      </TableCell>
                      {/* Tax */}
                      <TableCell sx={{ py: 0.5 }} align="right">
                        <TextField
                          size="small"
                          type="number"
                          inputProps={{ min: 0, step: 0.01, style: { textAlign: "right" } }}
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                          sx={{ width: 80 }}
                          value={entry.tax_rate === 0 ? "" : entry.tax_rate}
                          onChange={(e) => {
                            const num = parseFloat(e.target.value);
                            updateEntry(idx, "tax_rate", isNaN(num) ? 0 : num);
                          }}
                        />
                      </TableCell>
                      {/* Total */}
                      <TableCell sx={{ py: 0.5, fontWeight: 600 }} align="right">
                        {fmt(entry.total)}
                      </TableCell>
                      {/* Delete */}
                      <TableCell sx={{ py: 0.5 }} align="center">
                        <Tooltip title="Remove row">
                          <IconButton
                            size="small"
                            onClick={() => removeRow(idx)}
                            sx={{ color: "#ef4444" }}
                            disabled={entries.length === 1}
                          >
                            <FaTrash size={13} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* ── Bottom row: memo + totals ── */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 320px" }, gap: 3, alignItems: "start" }}>
          <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3, p: 3 }}>
            <Typography fontWeight={600} color="#0f172a" mb={1.5}>Memo / Notes</Typography>
            <TextField
              multiline rows={4} fullWidth
              placeholder="Internal notes or client-facing memo…"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
            />
          </Paper>

          <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3, p: 3 }}>
            <Typography fontWeight={600} color="#0f172a" mb={2}>Summary</Typography>
            {[
              { label: "Subtotal", value: fmt(subtotal) },
              { label: "Tax", value: fmt(taxTotal) },
            ].map((row) => (
              <Box key={row.label} sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography color="text.secondary" fontSize={14}>{row.label}</Typography>
                <Typography fontWeight={500} fontSize={14}>{row.value}</Typography>
              </Box>
            ))}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography color="text.secondary" fontSize={14}>Discount</Typography>
              <TextField
                size="small" type="number"
                inputProps={{ min: 0, step: 0.01, style: { textAlign: "right", width: 100 } }}
                value={discount === 0 ? "" : discount}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                onChange={(e) => {
                  const num = parseFloat(e.target.value);
                  setDiscount(isNaN(num) ? 0 : num);
                }}
              />
            </Box>
            <Box sx={{ borderTop: "2px solid #e2e8f0", pt: 1.5, mt: 1.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography fontWeight={700} fontSize={16} color="#0f172a">Grand Total</Typography>
                <Typography fontWeight={700} fontSize={18} color="#2f3891">{fmt(grandTotal)}</Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* ── Previous-invoice multi-match dialog ── */}
      <Dialog open={suggestOpen} onClose={() => setSuggestOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Select Matching Record</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <DialogContentText sx={{ px: 3, pt: 1, pb: 0.5, fontSize: 13, color: "text.secondary" }}>
            Multiple previous records found for this date. Choose one to auto-fill the form.
          </DialogContentText>
          <List disablePadding>
            {suggestions.map((s, i) => {
              const name = s.customer
                ? fmtCustomerName(s.customer, `Customer #${s.customer_id}`)
                : `Customer #${s.customer_id}`;
              return (
                <ListItemButton key={i} divider onClick={() => applyAutoFill(s)}>
                  <ListItemText
                    primary={name}
                    secondary={[s.contract_invoice_no, s.po_number].filter(Boolean).join(" · ")}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSuggestOpen(false)} sx={{ textTransform: "none" }}>Skip</Button>
        </DialogActions>
      </Dialog>

      {/* ── Contract selection dialog (multiple contracts) ── */}
      <Dialog open={contractSelectOpen} onClose={() => setContractSelectOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
          <AssignmentOutlinedIcon sx={{ color: "#2f3891" }} />
          Select Contract
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <DialogContentText sx={{ px: 3, pt: 1, pb: 0.5, fontSize: 13, color: "text.secondary" }}>
            Multiple active contracts found for this client and date. Select one to load billing entries.
          </DialogContentText>
          <List disablePadding>
            {pendingContracts.map((c, i) => (
              <ListItemButton
                key={i}
                divider
                onClick={() => pendingContractDate && loadAndApplyContract(c.id, pendingContractDate)}
                sx={{ py: 1.5 }}
              >
                <ListItemText
                  primary={<Typography fontWeight={600}>{c.contract_name}</Typography>}
                  secondary={
                    <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                      <Chip label={c.billing_type} size="small" sx={{ height: 18, fontSize: 10 }} />
                      <Typography variant="caption" color="text.secondary">
                        {c.start_date} → {c.end_date}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Chip
                    label={`${c.items?.length ?? 0} items`}
                    size="small"
                    sx={{ bgcolor: "#ede9fe", color: "#2f3891", fontWeight: 600 }}
                  />
                </ListItemSecondaryAction>
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setContractSelectOpen(false)} sx={{ textTransform: "none" }}>Skip</Button>
        </DialogActions>
      </Dialog>

      {/* ── Finalize confirmation dialog ── */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Finalize Invoice?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Finalizing will lock this invoice ({invoiceNo}). You can still record payments afterward. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => { setConfirmOpen(false); handleSave(pendingStatus); }}
            sx={{ textTransform: "none", bgcolor: "#2f3891", "&:hover": { bgcolor: "#2f3891" } }}
          >
            Finalize
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CreateContractInvoice;
